import { useCallback, useState } from 'react'

import { Box, Text, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { CompaniesLegalEntity, CompanyStepDataDocument } from 'generated/resources/graphql'
import { debounce } from 'lodash'

import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import type { WizardData } from 'modules/marketplace/lib/types'
import { DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

interface LegalEntityFillerProps extends WizardStepRenderParams<WizardData> {
  data: DatevWizardData
  legalEntity: Pick<CompaniesLegalEntity, 'id' | 'legalName'>
  onError: (error: boolean) => void
}

export const LegalEntityFiller = ({
  data,
  onDataChanged,
  legalEntity,
  onError,
}: LegalEntityFillerProps) => {
  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()
  const legalEntityKey = `legal_entity_${legalEntity.id}`
  const dataAnswer = data.answers[legalEntityKey]

  const [clientCode, setClientCode] = useState<string>(data.answers[legalEntityKey] || '')

  const onChange = (value: string) => {
    setClientCode(value)
  }

  const onBlur = () => {
    onLegalEntityChange(clientCode)
  }

  const onLegalEntityChange = useCallback(
    debounce((value: string) => {
      onDataChanged({
        ...data,
        answers: {
          ...data.answers,
          [legalEntityKey]: value.toString(),
        },
      })
    }, 1000),
    [data.answers, legalEntity.id]
  )

  // TODO: Handle loading and error states
  const { data: jobUuidData } = useQuery(CompanyStepDataDocument, {
    variables: {
      companyId: companyId,
      accessId: accessId,
      clientId: `${data.answers.consultant_id}-${dataAnswer}`,
      targetSystem: data.answers.payroll_software,
    },
    skip: !data.answers.consultant_id || !dataAnswer,
    onError: (error) => {
      onError(true)
      throw error
    },
    onCompleted: () => {
      onError(false)
    },
  })

  const jobUuid =
    jobUuidData?.integrationsDatevLauds.clientDataRequestsConnection?.nodes[0]?.jobUuid ?? ''

  const requestId =
    jobUuidData?.integrationsDatevLauds.clientDataRequestsConnection?.nodes[0]?.requestId ?? ''

  useEffect(() => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        [`job_uuid_${legalEntity.id}`]: jobUuid,
        [`request_id_${legalEntity.id}`]: requestId.toString(),
      },
    })
  }, [jobUuid, requestId])

  useUnsafeEffect(() => onLegalEntityChange.cancel(), [onLegalEntityChange])

  return (
    <Box gap="s24">
      <Box
        key={legalEntityKey}
        flexDirection="row"
        gap="s24"
        justifyContent="spaceBetween"
        maxWidth="s600"
      >
        <Text.Normal>{legalEntity.legalName}</Text.Normal>
        <Box width="s400">
          <TextField
            value={clientCode}
            onChange={onChange}
            placeholder={i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.legal_entity_text_field_placeholder'
            )}
            onBlur={onBlur}
          />
        </Box>
      </Box>
    </Box>
  )
}
